/**
 * useValuationDashboardData Hook
 * Custom hook for managing ValuationDashboard state and data fetching
 */

import { useState, useEffect, useRef } from 'react';
import {
  getBusinessProfile,
  getWealthGap,
  getValueGap,
  getProfitGap,
  getValuationHistory,
  getIndustryData
} from '../services/api';

/**
 * Helper function to get latest value from multi-year data
 * @param {*} field - Field value (can be object or direct value)
 * @returns {number} Latest value
 */
const getLatestValue = (field) => {
  if (!field || typeof field !== 'object') return 0;
  const years = Object.keys(field).sort();
  if (years.length === 0) return 0;
  const latestYear = years[years.length - 1];
  return parseFloat(field[latestYear]) || 0;
};

/**
 * Custom hook for ValuationDashboard data management
 * @returns {Object} State and handlers for ValuationDashboard
 */
export const useValuationDashboardData = () => {
  // Loading state
  const [loading, setLoading] = useState(true);
  const isInitialLoad = useRef(true);

  // Core data state
  const [latestValuation, setLatestValuation] = useState(null);
  const [valuationHistory, setValuationHistory] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [wealthGap, setWealthGap] = useState(null);
  const [valueGap, setValueGap] = useState(null);
  const [profitGap, setProfitGap] = useState(null);
  const [industryData, setIndustryData] = useState(null);

  // UI state
  const [editMode, setEditMode] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [viewingHistoricalValuation, setViewingHistoricalValuation] = useState(false);

  // Valuation type toggle (simple or advanced)
  const [valuationType, setValuationType] = useState(() => {
    const userId = JSON.parse(localStorage.getItem('user'))?.id || 'default';
    return localStorage.getItem(`valuationType_${userId}`) || 'simple';
  });

  // Interactive controls
  const [growthRate, setGrowthRate] = useState(15);
  const [profitMargin, setProfitMargin] = useState(20);
  const [ebitdaMargin, setEbitdaMargin] = useState(0);
  const [industryMultiple, setIndustryMultiple] = useState(12);
  const [discountRate, setDiscountRate] = useState(15);
  const [netWorthGrowthRate, setNetWorthGrowthRate] = useState(5);
  const [exitValueGrowthRate, setExitValueGrowthRate] = useState(10);
  const [wealthGapGrowthRate, setWealthGapGrowthRate] = useState(8);

  // Financial metrics (editable)
  const [financials, setFinancials] = useState({
    revenue: 0,
    ebitda: 0,
    grossProfit: 0,
    assetValue: 0,
    grossMargin: 0,
    profitMargin: 0,
    netIncome: 0
  });

  // Goals
  const [goals, setGoals] = useState({
    targetValuation: 0,
    lifestyleNeeds: 0,
    minimumExit: 0
  });

  /**
   * Fetch all dashboard data from API
   */
  const fetchAllData = async () => {
    setLoading(true);
    try {
      // On initial load, check if we should auto-switch to show the latest valuation
      let typeToFetch = valuationType;
      if (isInitialLoad.current) {
        // Fetch ALL valuation history first (no type filter) to get the absolute latest
        const allValuationsResult = await getValuationHistory();

        if (allValuationsResult.success) {
          const allValuations = allValuationsResult.data.valuations || [];

          // If we have valuations, auto-switch the toggle to match the latest
          if (allValuations.length > 0) {
            const absoluteLatest = allValuations[0];
            const latestType = absoluteLatest.type || 'simple';

            // If the toggle doesn't match the latest valuation type, update it
            if (latestType !== valuationType) {
              typeToFetch = latestType;
              setValuationType(latestType);
              const userId = JSON.parse(localStorage.getItem('user'))?.id || 'default';
              localStorage.setItem(`valuationType_${userId}`, latestType);
            }
          }
        }
        isInitialLoad.current = false;
      }

      // Fetch filtered valuations based on the valuationType
      const valuationResult = await getValuationHistory(typeToFetch);
      const valuations = valuationResult.success ? (valuationResult.data.valuations || []) : [];
      setValuationHistory(valuations);

      // Fetch business profile
      const profileResult = await getBusinessProfile();
      if (profileResult.success && profileResult.data.profile) {
        setBusinessProfile(profileResult.data.profile);
      }

      // Fetch wealth gap
      const wealthGapResult = await getWealthGap();
      if (wealthGapResult.success && wealthGapResult.data?.wealth_gap) {
        setWealthGap(wealthGapResult.data.wealth_gap);
      }

      // Fetch value gap
      const valueGapResult = await getValueGap();
      if (valueGapResult.success && valueGapResult.data?.value_gap) {
        setValueGap(valueGapResult.data.value_gap);
      }

      // Fetch profit gap
      const profitGapResult = await getProfitGap();
      if (profitGapResult.success && profitGapResult.data?.profit_gap) {
        setProfitGap(profitGapResult.data.profit_gap);
      }


      if (valuations.length > 0) {
        const latest = valuations[0];

        setLatestValuation(latest);

        // Parse and extract financial data from input_data
        try {
          let inputs = null;

          if (latest.input_data) {
            inputs = typeof latest.input_data === 'string'
              ? JSON.parse(latest.input_data)
              : latest.input_data;
          }

          if (inputs) {
            let revenue, ebitda, netIncome, totalAssets, grossProfit;

            // Handle different data formats for simple vs advanced valuations
            if (latest.type === 'advanced') {
              // Advanced valuation has multi-year data in nested objects
              revenue = getLatestValue(inputs.revenue);
              ebitda = getLatestValue(inputs.ebitda);
              grossProfit = getLatestValue(inputs.gross_profit);
              netIncome = getLatestValue(inputs.profit_after_tax) || getLatestValue(inputs.net_income);
              totalAssets = getLatestValue(inputs.total_assets);
            } else {
              // Simple valuation has flat structure
              // Parse values, handling both string and number types
              revenue = parseFloat(inputs.revenue) || 0;
              ebitda = parseFloat(inputs.ebitda) || 0;
              grossProfit = parseFloat(inputs.gross_profit) || 0;
              netIncome = parseFloat(inputs.net_income) || parseFloat(inputs.profit_after_tax) || 0;
              totalAssets = parseFloat(inputs.total_assets) || 0;

            }

            const calculatedMargin = revenue && ebitda ? Math.round((ebitda / revenue) * 100) : 0;

            const financialData = {
              revenue: revenue,
              ebitda: ebitda,
              grossProfit: grossProfit || ebitda,
              assetValue: totalAssets,
              grossMargin: calculatedMargin,
              profitMargin: revenue && netIncome ? Math.round((netIncome / revenue) * 100) : 0,
              netIncome: netIncome
            };

            setFinancials(financialData);
            setEbitdaMargin(calculatedMargin);

            // Fetch industry multiple if industry_id is present
            if (inputs.industry_id) {
              try {
                const industryResult = await getIndustryData(inputs.industry_id);
                if (industryResult.success && industryResult.data.industry) {
                  const industry = industryResult.data.industry;
                  setIndustryData(industry);
                  const evEbitdaMedian = industry.ev_ebitda_median || 12;
                  setIndustryMultiple(evEbitdaMedian);
                }
              } catch (error) {
                console.error('Error fetching industry multiple:', error);
                setIndustryMultiple(12);
              }
            }
          }
        } catch (error) {
          console.error('[Dashboard] Error parsing valuation input_data:', error);
        }

        // Set growth rate from profile if available
        if (profileResult.data?.profile?.growth_rate) {
          setGrowthRate(parseFloat(profileResult.data.profile.growth_rate));
        }
      } else {
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  /**
   * Handle valuation type toggle
   */
  const handleToggleValuationType = (type) => {
    setValuationType(type);
    const userId = JSON.parse(localStorage.getItem('user'))?.id || 'default';
    localStorage.setItem(`valuationType_${userId}`, type);
    setViewingHistoricalValuation(false);
  };

  /**
   * Handle going back to current valuation
   */
  const handleBackToCurrent = () => {
    setViewingHistoricalValuation(false);
    fetchAllData();
  };

  /**
   * Handle deleting a valuation
   */
  const handleDeleteValuation = (valuationId) => {
    setValuationHistory(prev => prev.filter(v => v.id !== valuationId));
    if (latestValuation?.id === valuationId) {
      setLatestValuation(null);
      setViewingHistoricalValuation(false);
    }
  };

  /**
   * Handle loading a historical valuation
   */
  const handleLoadHistoricalValuation = async (valuation) => {
    setLatestValuation(valuation);
    setViewingHistoricalValuation(true);
    setShowHistoryModal(false);

    // Parse and set financials from historical valuation
    try {
      let inputs = null;
      if (valuation.input_data) {
        inputs = typeof valuation.input_data === 'string'
          ? JSON.parse(valuation.input_data)
          : valuation.input_data;
      }

      if (inputs) {
        const revenue = getLatestValue(inputs.revenue);
        const ebitda = getLatestValue(inputs.ebitda);
        const netIncome = getLatestValue(inputs.net_income || inputs.profit_after_tax);
        const totalAssets = getLatestValue(inputs.total_assets);
        const grossProfit = getLatestValue(inputs.gross_profit);

        const calculatedMargin = revenue && ebitda ? Math.round((ebitda / revenue) * 100) : 0;

        setFinancials({
          revenue,
          ebitda,
          grossProfit: grossProfit || ebitda,
          assetValue: totalAssets,
          grossMargin: calculatedMargin,
          profitMargin: revenue && netIncome ? Math.round((netIncome / revenue) * 100) : 0,
          netIncome
        });

        setEbitdaMargin(calculatedMargin);

        // Fetch industry data if available
        if (inputs.industry_id) {
          try {
            const industryResult = await getIndustryData(inputs.industry_id);
            if (industryResult.success && industryResult.data.industry) {
              const industry = industryResult.data.industry;
              setIndustryData(industry);
              setIndustryMultiple(industry.ev_ebitda_median || 12);
            }
          } catch (error) {
            console.error('Error fetching industry data:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading historical valuation:', error);
    }
  };

  // Fetch data on mount and when valuationType changes
  useEffect(() => {
    fetchAllData();
  }, [valuationType]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // State
    loading,
    latestValuation,
    valuationHistory,
    businessProfile,
    wealthGap,
    valueGap,
    profitGap,
    industryData,
    editMode,
    showHistoryModal,
    viewingHistoricalValuation,
    valuationType,
    growthRate,
    profitMargin,
    ebitdaMargin,
    industryMultiple,
    discountRate,
    netWorthGrowthRate,
    exitValueGrowthRate,
    wealthGapGrowthRate,
    financials,
    goals,

    // Setters
    setEditMode,
    setShowHistoryModal,
    setViewingHistoricalValuation,
    setGrowthRate,
    setProfitMargin,
    setEbitdaMargin,
    setIndustryMultiple,
    setDiscountRate,
    setNetWorthGrowthRate,
    setExitValueGrowthRate,
    setWealthGapGrowthRate,
    setFinancials,
    setGoals,

    // Handlers
    fetchAllData,
    handleToggleValuationType,
    handleBackToCurrent,
    handleDeleteValuation,
    handleLoadHistoricalValuation
  };
};
